import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { loadStripe } from "@stripe/stripe-js";
import cn from "classnames";
import Button from "../common/Button";

export type SeatGrant = { user_id: string; granted_at: string; name: string | null; email: string };
export type SeatPool = {
  period: { interval: "month" | "year"; label: string; startMonth: string; endMonth: string };
  seatsPurchased: number;
  pct: number;
  creditMinutes: number;
};
export type SeatAddon = "investigations" | "video";
export type SeatData = {
  label?: string;
  allowanceMinutes?: number;
  seatsPurchased: number;
  seatsUsed: number;
  status: string | null;
  billingInterval: "month" | "year";
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  source: string | null;
  grants: SeatGrant[];
  pricePerSeat: number;
  pricePerSeatAnnual: number;
  annualAvailable: boolean;
  // Shared hours: every seat adds 50 a month (× 12 on an annual plan),
  // packs add more for the current period. Only how close the department
  // is to the allowance is shown, never a running count.
  pool: SeatPool | null;
  hourPack: { hours: number; price: number; available: boolean };
};

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
    "pk_live_51PydONG4bf6K0RLmdfEJzwpzVpDCNNdt0HjC5sn5QokyLJij6TflI306Xoi69SSqHORZ9cPnt3xw2leQx9rrYb0B00nka5yr7n"
);

// Investigator seats for the Officers tab. Seats are bought here and
// assigned from the role dropdown in the officer table (choosing
// "Investigator" grants one; choosing Officer/Records on an investigator
// revokes it), so this hook is shared by the bar and the table.
export function useInvestigationSeats(addon: SeatAddon = "investigations") {
  const [data, setData] = useState<SeatData | null>(null);
  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get<SeatData>(`/api/agency/addons/${addon}`);
      setData(data);
    } catch {
      toast.error(addon === "video" ? "Failed to load footage seats" : "Failed to load investigator seats");
    }
  }, [addon]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { data, reload };
}

// Copy per add-on: the Investigations pool is audio hours shared by
// investigators; the video pool is hours of analyzed footage per officer.
const ADDON_COPY: Record<SeatAddon, { title: string; none: string; hours: (n: number, annual: boolean) => string }> = {
  investigations: {
    title: "Investigator seats",
    none: "None purchased. Set an officer's role to Investigator once you add seats.",
    hours: (_n, annual) => `Every seat adds 50 hours of audio a month${annual ? ", pooled across the billing year" : ""}, shared by every investigator.`,
  },
  video: {
    title: "Footage analysis seats",
    none: "None purchased. Turn on Footage for an officer once you add seats.",
    hours: (n, annual) => `Every seat adds ${n} hours of analyzed body cam or surveillance footage a month${annual ? ", pooled across the billing year" : ""}, shared by every officer with the feature.`,
  },
};

export const InvestigationSeatsBar: React.FC<{ data: SeatData | null; addon?: SeatAddon }> = ({ data, addon = "investigations" }) => {
  const copy = ADDON_COPY[addon];
  const [seats, setSeats] = useState(1);
  const [packs, setPacks] = useState(1);
  const [busy, setBusy] = useState(false);
  const [busyHours, setBusyHours] = useState(false);
  const [intervalChoice, setIntervalChoice] = useState<"month" | "year" | null>(null);

  // Seats already on a plan stay on that interval; a first purchase can pick.
  const locked = Boolean(data && data.seatsPurchased > 0);
  const interval: "month" | "year" = locked ? data!.billingInterval : intervalChoice ?? (data?.annualAvailable ? "year" : "month");

  const buy = async () => {
    setBusy(true);
    try {
      const { data: res } = await axios.post("/api/agency/purchase-addon-seats", { seats, addon, interval });
      const stripe = await stripePromise;
      await stripe?.redirectToCheckout({ sessionId: res.sessionId });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  const buyHours = async () => {
    setBusyHours(true);
    try {
      const { data: res } = await axios.post("/api/agency/purchase-addon-hours", { packs, addon });
      const stripe = await stripePromise;
      await stripe?.redirectToCheckout({ sessionId: res.sessionId });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Could not start checkout");
    } finally {
      setBusyHours(false);
    }
  };

  if (!data) return <div className="animate-pulse h-16 bg-base-300 rounded-xl mt-6" />;
  const remaining = Math.max(0, data.seatsPurchased - data.seatsUsed);
  const pool = data.pool;
  const span = (pool?.period.interval ?? interval) === "year" ? "billing year" : "month";
  const nearLimit = pool != null && pool.pct >= 80;
  const atLimit = pool != null && pool.pct >= 100;
  const priceLabel = interval === "year" ? `$${data.pricePerSeatAnnual}/seat/yr` : `$${data.pricePerSeat}/seat/mo`;

  return (
    <div className="mt-6 rounded-xl border border-base-300 bg-base-100 px-4 py-3">
    <div className="flex items-center gap-4 flex-wrap">
      <div className="min-w-0">
        <p className="m-0 text-sm font-bold text-base-content">
          {copy.title}
          {(data.source === "trial" || data.source === "comp") && (
            <span className="ml-2 text-[11px] font-medium text-blue-600 dark:text-blue-300">{data.source === "trial" ? "evaluation" : "complimentary"}</span>
          )}
        </p>
        <p className="m-0 text-sm text-base-content/60">
          {data.seatsPurchased === 0 ? (
            <>{copy.none}</>
          ) : (
            <>
              <span className="font-semibold text-base-content/80">{data.seatsUsed}</span> of {data.seatsPurchased} assigned
              <span className="text-base-content/40 mx-1.5">·</span>
              {remaining} available
              <span className="text-base-content/40 mx-1.5">·</span>
              {data.billingInterval === "year" ? "billed annually" : "billed monthly"}
              {data.currentPeriodEnd && (
                <>
                  <span className="text-base-content/40 mx-1.5">·</span>
                  renews {new Date(data.currentPeriodEnd).toLocaleDateString()}
                </>
              )}
              {data.status && data.status !== "active" && data.status !== "trialing" && (
                <span className="ml-1.5 text-red-600 dark:text-red-400">({data.status})</span>
              )}
            </>
          )}
        </p>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {!locked && data.annualAvailable && (
          <div className="flex rounded-lg border border-base-300 overflow-hidden text-xs" role="radiogroup" aria-label="Billing interval">
            {(["year", "month"] as const).map((i) => (
              <button
                key={i}
                type="button"
                role="radio"
                aria-checked={interval === i}
                onClick={() => setIntervalChoice(i)}
                className={cn("px-2.5 py-1.5", interval === i ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 font-medium" : "text-base-content/70 hover:bg-base-200")}
              >
                {i === "year" ? "Annual" : "Monthly"}
              </button>
            ))}
          </div>
        )}
        <span className="text-sm text-base-content/60 whitespace-nowrap">{priceLabel}</span>
        <input
          type="number"
          min={1}
          max={500}
          className="input input-sm input-bordered bg-base-100 w-20"
          value={seats}
          onChange={(e) => setSeats(Math.min(500, Math.max(1, Number(e.target.value) || 1)))}
          aria-label="Seats to add"
        />
        <Button variant="primary" size="sm" onClick={buy} loading={busy}>
          Add seats
        </Button>
      </div>
    </div>
      {pool && (
        <div className="mt-3 pt-3 border-t border-base-300 flex items-center gap-4 flex-wrap">
          <div className="min-w-0 flex-1 basis-64">
            <p className={cn("m-0 text-sm", atLimit ? "text-red-700 dark:text-red-400" : nearLimit ? "text-amber-700 dark:text-amber-400" : "text-base-content/60")}>
              {atLimit
                ? `Your department has used all of its ${data.label || "Investigations"} hours for this ${span}. Uploads continue; add hours or seats if you expect more.`
                : nearLimit
                  ? `Your department has used most of its ${data.label || "Investigations"} hours for this ${span}.`
                  : copy.hours(Math.round((data.allowanceMinutes || 600) / 60), pool.period.interval === "year")}
            </p>
          </div>
          {data.hourPack.available && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm text-base-content/60 whitespace-nowrap">
                ${data.hourPack.price} per {data.hourPack.hours} hours
              </span>
              <input
                type="number"
                min={1}
                max={50}
                className="input input-sm input-bordered bg-base-100 w-20"
                value={packs}
                onChange={(e) => setPacks(Math.min(50, Math.max(1, Number(e.target.value) || 1)))}
                aria-label="Hour packs to add"
              />
              <Button variant="secondary" size="sm" onClick={buyHours} loading={busyHours}>
                Add hours
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InvestigationSeatsBar;
